export type TemplateItem = {
  id: string;
  name: string;
  description?: string;
  category: 'business' | 'artist';
  active: boolean;
  approved?: boolean;
  previewUrl?: string;
  bundleUrl?: string;
  componentKey?: string;
  approvalStatus?: 'pending' | 'approved' | 'rejected';
  submittedById?: string;
  submittedByEmail?: string;
  submittedAt?: string;
  reviewedBy?: string;
  reviewedAt?: string;
  rejectionReason?: string;
  createdAt: string;
  updatedAt: string;
};

export async function listTemplates(params?: { approvedOnly?: boolean; activeOnly?: boolean; category?: 'business'|'artist' }) {
  const controller = new AbortController();
  const t = setTimeout(() => controller.abort(), 5000);
  const q = new URLSearchParams();
  if (params?.approvedOnly) q.set('approvedOnly', 'true');
  if (params?.activeOnly) q.set('activeOnly', 'true');
  if (params?.category) q.set('category', params.category);
  const token = (typeof window !== 'undefined' ? localStorage.getItem('dev_admin_token') : '') || '';
  const res = await fetch(`/api/templates${q.toString() ? `?${q}` : ''}`, { credentials: 'same-origin', headers: token ? { Authorization: `Bearer ${token}` } : undefined as any, signal: controller.signal });
  clearTimeout(t);
  if (!res.ok) throw new Error('Failed to fetch templates');
  return (await res.json()) as TemplateItem[];
}

export async function listTemplatesMeta(params?: { approvedOnly?: boolean; activeOnly?: boolean; category?: 'business'|'artist' }) {
  const controller = new AbortController();
  const t = setTimeout(() => controller.abort(), 5000);
  const q = new URLSearchParams();
  if (params?.approvedOnly) q.set('approvedOnly', 'true');
  if (params?.activeOnly) q.set('activeOnly', 'true');
  if (params?.category) q.set('category', params.category);
  // Ask server for TemplateMeta format
  q.set('format', 'meta');
  const token = (typeof window !== 'undefined' ? localStorage.getItem('dev_admin_token') : '') || '';
  const res = await fetch(`/api/templates${q.toString() ? `?${q}` : ''}`, { credentials: 'same-origin', headers: token ? { Authorization: `Bearer ${token}` } : undefined as any, signal: controller.signal });
  clearTimeout(t);
  if (!res.ok) throw new Error('Failed to fetch templates');
  return (await res.json()) as TemplateMeta[];
}

export async function uploadTemplateBundle(file: File): Promise<{ url: string }> {
  const fd = new FormData();
  fd.append('bundle', file);
  const token = (typeof window !== 'undefined' ? localStorage.getItem('dev_admin_token') : '') || '';
  const res = await fetch('/api/admin/templates/uploadBundle', { method: 'POST', body: fd, credentials: 'same-origin', headers: token ? { Authorization: `Bearer ${token}` } : undefined as any });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = (body && (body.error || body.message)) || `Upload failed (${res.status})`;
    throw new Error(msg);
  }
  return body as { url: string };
}

export async function createTemplate(item: Partial<TemplateItem>) {
  const token = (typeof window !== 'undefined' ? localStorage.getItem('dev_admin_token') : '') || '';
  const res = await fetch('/api/admin/templates', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    credentials: 'same-origin',
    body: JSON.stringify(item),
  });
  const body = await res.json();
  if (!res.ok) throw body;
  return body as TemplateItem;
}

export async function updateTemplate(id: string, updates: Partial<TemplateItem>) {
  const token = (typeof window !== 'undefined' ? localStorage.getItem('dev_admin_token') : '') || '';
  const res = await fetch(`/api/admin/templates/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    credentials: 'same-origin',
    body: JSON.stringify(updates),
  });
  const body = await res.json();
  if (!res.ok) throw body;
  return body as TemplateItem;
}

export async function deleteTemplate(id: string) {
  const token = (typeof window !== 'undefined' ? localStorage.getItem('dev_admin_token') : '') || '';
  const res = await fetch(`/api/admin/templates/${encodeURIComponent(id)}`, { method: 'DELETE', credentials: 'same-origin', headers: token ? { Authorization: `Bearer ${token}` } : undefined as any });
  if (!res.ok) throw await res.json();
  return true;
}

export async function submitTemplateForApproval(id: string) {
  const token = (typeof window !== 'undefined' ? localStorage.getItem('dev_admin_token') : '') || '';
  const res = await fetch(`/api/admin/templates/${encodeURIComponent(id)}/submit`, {
    method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }
  });
  const body = await res.json().catch(()=>({}));
  if (!res.ok) throw body;
  return body as TemplateItem;
}

export async function approveTemplate(id: string) {
  const token = (typeof window !== 'undefined' ? localStorage.getItem('dev_admin_token') : '') || '';
  const res = await fetch(`/api/admin/templates/${encodeURIComponent(id)}/approve`, {
    method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }
  });
  const body = await res.json().catch(()=>({}));
  if (!res.ok) throw body;
  return body as TemplateItem;
}

export async function rejectTemplate(id: string, reason = '') {
  const token = (typeof window !== 'undefined' ? localStorage.getItem('dev_admin_token') : '') || '';
  const res = await fetch(`/api/admin/templates/${encodeURIComponent(id)}/reject`, {
    method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }, body: JSON.stringify({ reason })
  });
  const body = await res.json().catch(()=>({}));
  if (!res.ok) throw body;
  return body as TemplateItem;
}

export async function uploadTCard(file: File, overrides?: Partial<TemplateItem>): Promise<TemplateItem> {
  const fd = new FormData();
  fd.append('tcard', file);
  if (overrides) {
    if (overrides.name) fd.append('name', String(overrides.name));
    if (overrides.description) fd.append('description', String(overrides.description));
    if (overrides.category) fd.append('category', String(overrides.category));
    if (overrides.componentKey && overrides.componentKey !== 'auto') {
      fd.append('componentKey', String(overrides.componentKey));
    }
  }
  const token = (typeof window !== 'undefined' ? localStorage.getItem('dev_admin_token') : '') || '';
  const res = await fetch('/api/admin/templates/uploadTCard', { method: 'POST', body: fd, credentials: 'same-origin', headers: token ? { Authorization: `Bearer ${token}` } : undefined as any });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = (body && (body.error || body.message)) || `Upload failed (${res.status})`;
    throw new Error(msg);
  }
  return body as TemplateItem;
}

export async function previewTCard(file: File, overrides?: Partial<TemplateItem>): Promise<Partial<TemplateItem>> {
  const fd = new FormData();
  fd.append('tcard', file);
  if (overrides) {
    if (overrides.name) fd.append('name', String(overrides.name));
    if (overrides.description) fd.append('description', String(overrides.description));
    if (overrides.category) fd.append('category', String(overrides.category));
    if (overrides.componentKey && overrides.componentKey !== 'auto') {
      fd.append('componentKey', String(overrides.componentKey));
    }
  }
  const token = (typeof window !== 'undefined' ? localStorage.getItem('dev_admin_token') : '') || '';
  const res = await fetch('/api/admin/templates/previewTCard', { method: 'POST', body: fd, credentials: 'same-origin', headers: token ? { Authorization: `Bearer ${token}` } : undefined as any });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = (body && (body.error || body.message)) || `Preview failed (${res.status})`;
    throw new Error(msg);
  }
  return body as Partial<TemplateItem>;
}

export type TemplateRenderer = "static-v1" | "dynamic-v1";

export type TemplateMeta = {
  id: string;
  name: string;
  description?: string;
  category: "Business" | "Artist";
  renderer: TemplateRenderer;
  componentKey: string;
  spec?: any;
  coverUrl?: string;
  assets?: Record<string, string>;
  version?: string;
  status: "uploaded" | "active" | "inactive";
};

export type TemplateFetchResponse = {
  id: string;
  meta: any;
  spec: any | null;
  previewUrl?: string;
  bundleUrl?: string;
};

export async function getTemplate(id: string): Promise<TemplateFetchResponse> {
  const res = await fetch(`/api/templates/${encodeURIComponent(id)}`, { credentials: 'same-origin' });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || `Failed to fetch template ${id}`);
  return body as TemplateFetchResponse;
}

// Admin utility: reindex .tcard bundles from server uploads into registry
export async function reindexTemplates(): Promise<{ ok: boolean; imported: number; details: Array<{ id: string; name: string; bundle: string }> }> {
  const token = (typeof window !== 'undefined' ? localStorage.getItem('dev_admin_token') : '') || '';
  const res = await fetch('/api/admin/templates/reindex', { method: 'POST', credentials: 'same-origin', headers: token ? { Authorization: `Bearer ${token}` } : undefined as any });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || 'Reindex failed');
  return body as any;
}
